import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  CircularProgress,
} from '@mui/material';
import api from '../../services/api';

const AgentVisitsMeetingsReportPage = ({ dateRangeState, selectedTeams = [] }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
        const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');

        const requestData = {
          date_range: {
            start_date: startDate,
            end_date: endDate,
          },
        };

        if (selectedTeams && selectedTeams.length > 0) {
          requestData.team_filter = selectedTeams;
        }

        const response = await api.post('/report/agent/visits-meetings/', requestData);
        const data = response.data || {};
        setRows(data.results || []);
      } catch (error) {
        console.error('Error fetching agent visits & meetings:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRangeState, selectedTeams]);
  return (
    <Box sx={{ flexGrow: 1, pt: 0, pb: 3 }}>
      <Box
        sx={{
          mb: 2,
          px: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}
          >
            Agent Visits &amp; Meetings Report
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520 }}>
            Performance of agent visits and meetings, including show rates and conversions.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
          }}
          // TODO: Wire this to your export logic (CSV / XLSX / PDF)
          onClick={() => {
            console.log('Export Agent Visits & Meetings clicked');
          }}
        >
          Export
        </Button>
      </Box>
      <Box sx={{ px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: (theme) => theme.shadows[2],
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <TableContainer
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.2)',
              },
            }}
          >
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[50],
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Agent Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Visits Scheduled</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Unique Leads Visit Scheduled</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Visits Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Unique Leads Visit Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Visit No-Shows</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Unique Leads Visit No-Shows</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Meetings Scheduled</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Unique Leads Meetings Scheduled</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Meetings Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Unique Leads Meetings Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Meeting No-Shows</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Meetings No-Shows – Unique Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Converted After Meeting</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Converted After Visit</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Not Interested After Visit</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Not Interested After Meeting</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={17} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={17} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected period
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={(theme) => ({
                        height: theme.spacing(7),
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:last-child td, &:last-child th': { border: 0 },
                      })}
                    >
                      <TableCell>{row.agentName}</TableCell>
                      <TableCell align="right">{row.visitsScheduled}</TableCell>
                      <TableCell align="right">{row.uniqueLeadsVisitScheduled}</TableCell>
                      <TableCell align="right">{row.visitsCompleted}</TableCell>
                      <TableCell align="right">{row.uniqueLeadsVisitCompleted}</TableCell>
                      <TableCell align="right">{row.visitNoShows}</TableCell>
                      <TableCell align="right">{row.uniqueLeadsVisitNoShows}</TableCell>
                      <TableCell align="right">{row.meetingsScheduled}</TableCell>
                      <TableCell align="right">{row.uniqueLeadsMeetingsScheduled}</TableCell>
                      <TableCell align="right">{row.meetingsCompleted}</TableCell>
                      <TableCell align="right">{row.uniqueLeadsMeetingsCompleted}</TableCell>
                      <TableCell align="right">{row.meetingNoShows}</TableCell>
                      <TableCell align="right">{row.meetingsNoShowsUniqueLeads}</TableCell>
                      <TableCell align="right">{row.leadsConvertedAfterMeeting}</TableCell>
                      <TableCell align="right">{row.leadsConvertedAfterVisit}</TableCell>
                      <TableCell align="right">{row.leadsNotInterestedAfterVisit}</TableCell>
                      <TableCell align="right">{row.leadsNotInterestedAfterMeeting}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default AgentVisitsMeetingsReportPage;


